<template>
    <div ref="wrapRef" class="datepicker-input__wrap">
        <date-time-input
            :modelValue="formattedDate"
            :placeholder="props.placeholder"
            :format="props.format"
            @focus="showDatePicker = true"
            :library="props.library"
            :error="props.error"
            :size="props.size"
        />
        <teleport to="body" :disabled="!props.teleport">
            <div
                v-if="showDatePicker"
                ref="popupRef"
                class="datepicker-input__datepicker"
                :class="{ 'datepicker-input__datepicker--teleported': props.teleport }"
                :style="props.teleport ? position : undefined"
            >
                <date-time-picker :modelValue="date" mode="date" @update:modelValue="emitModelValue" />
            </div>
        </teleport>
    </div>
</template>

<script lang="ts" setup>
import DateTimeInput from './DateTimeInput.vue';
import DateTimePicker from './DateTimePicker.vue';
import { useEventListener } from '@vueuse/core';
import dayjs from '~/utils/dayjs';
import { ref, watch, computed, reactive, watchEffect } from 'vue';

interface Props {
    modelValue?: string;
    placeholder?: string;
    format?: string;
    library?: boolean;
    error?: string;
    teleport?: boolean;
    size?: string;
}

const props = withDefaults(defineProps<Props>(), {
    format: 'MM/DD/YYYY',
    teleport: false,
});

const emit = defineEmits<{
    'update:modelValue': [value: string];
}>();

const showDatePicker = ref(false);
const date = ref<Date | null>(null);
const wrapRef = ref<HTMLElement | null>(null);
const popupRef = ref<HTMLElement | null>(null);

const position = reactive({
    top: '0px',
    left: '0px',
});

const updatePosition = () => {
    if (!props.teleport || !showDatePicker.value || !wrapRef.value) {
        return;
    }

    const rect = wrapRef.value.getBoundingClientRect();
    position.top = `${rect.bottom}px`;
    position.left = `${rect.left}px`;
};

watchEffect(updatePosition);
useEventListener(window, 'scroll', updatePosition, true);
useEventListener(window, 'resize', updatePosition);

useEventListener(window, 'pointerdown', (event) => {
    if (!showDatePicker.value) {
        return;
    }

    const target = event.target as Node | null;
    if (wrapRef.value?.contains(target) || popupRef.value?.contains(target)) {
        return;
    }

    showDatePicker.value = false;
});

watch(() => props.modelValue, (newValue) => {
    if (newValue === undefined || newValue === '') {
        date.value = null;
        return;
    }
    const parsed = dayjs.utc(newValue, 'YYYY-MM-DD');
    date.value = parsed.isValid() ? parsed.toDate() : null;
}, { immediate: true });

const formattedDate = computed(() =>
    date.value ? dayjs.utc(date.value).format(props.format) : undefined,
);

const emitModelValue = (event: Date | string) => {
    emit('update:modelValue', dayjs.utc(event).format('YYYY-MM-DD'));
    showDatePicker.value = false;
};
</script>

<style>
.datepicker-input__datepicker {
  position: absolute;
  z-index: 11;
  width: 275px;
}
.datepicker-input__datepicker--teleported {
  position: fixed;
  z-index: 1000;
}
.datepicker-input__wrap {
  position: relative;
}
</style>
